import { useMemo } from 'react';
import clsx from 'clsx';
import Dropdown from '../../../../../../../common/_custom/Dropdown/Dropdown';
import { DropdownOption } from '../../../../../../../common/_custom/Dropdown/dropdownTypes';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../utils/dateUtils';
import {
    ALL_FILES_LABEL,
    ALL_FILES_OPTION_ID,
    PROVISIONAL_FILE_STATUS_CHIP,
    PROVISIONAL_FILE_UNKNOWN_STATUS_CHIP,
    PROVISIONAL_FILE_VIEW_STATUS,
    VIEW_DOCUMENT_LABEL,
} from './constants';
import { ProvisionalFileItem } from './types';
import styles from './ProvisionalFileView.module.scss';

interface ProvisionalFileSelectProps {
    files: ProvisionalFileItem[];
    selectedFile?: ProvisionalFileItem;
    onSelect: (docId: string | null) => void;
    hideLabel?: boolean;
}

const ALL_FILES_OPTION: DropdownOption = { id: ALL_FILES_OPTION_ID, label: ALL_FILES_LABEL };

const toOption = (file: ProvisionalFileItem): DropdownOption => {
    const date = formatDateByFormat(file.uploadedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);
    const chip =
        file.viewStatus !== PROVISIONAL_FILE_VIEW_STATUS.VIEWABLE
            ? PROVISIONAL_FILE_STATUS_CHIP[file.viewStatus] ?? PROVISIONAL_FILE_UNKNOWN_STATUS_CHIP
            : undefined;
    const secondaryLabel =
        date || chip ? (
            <>
                {date}
                {chip && <span className={clsx(styles.StatusChip, { [styles.ChipSpaced]: !!date })}>{chip}</span>}
            </>
        ) : undefined;
    // The trigger shows name + date only; the status chip is a list-row affordance.
    return { id: file.docId, label: file.displayName, secondaryLabel, selectedSecondaryLabel: date };
};

const ProvisionalFileSelect = ({ files, selectedFile, onSelect, hideLabel = false }: ProvisionalFileSelectProps) => {
    // Server order is kept: the engine sorts newest upload first.
    const options = useMemo(() => [ALL_FILES_OPTION, ...files.map(toOption)], [files]);
    const selectedOption = useMemo(
        () => options.find((option) => option.id === (selectedFile?.docId ?? ALL_FILES_OPTION_ID)) ?? ALL_FILES_OPTION,
        [options, selectedFile],
    );

    return (
        <div className={styles.FileControls}>
            {!hideLabel && <span className={styles.FileLabel}>{VIEW_DOCUMENT_LABEL}</span>}
            <div className={styles.FileSelect}>
                <Dropdown
                    optionsList={options}
                    selectedOption={selectedOption}
                    itemClickHandler={(option) => onSelect(option.id === ALL_FILES_OPTION_ID ? null : String(option.id))}
                    variant="white"
                    hideClearIcon
                    showFloatingLabel={false}
                />
            </div>
        </div>
    );
};

export default ProvisionalFileSelect;
